import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IHighlightDataModel,
  IHighlightModel,
  IPageDataModel,
} from "../../tsmodels/SharedModel";
import "./sharedStyles.scss";
import {
  faAngleUp,
  faAngleDown,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { scaleTransformOptions } from "../../data/DocumentsData";

interface IDocumentRenderProps {
  pages: IPageDataModel[];
  highlights: any;
  selectedPage?: number | null;
  selectedHighlight?: any;
}

const DocumentRender = (props: IDocumentRenderProps) => {
  // props
  const { pages, highlights, selectedPage, selectedHighlight } = props;

  //refs

  const parentRef = useRef<HTMLDivElement | null>(null);

  // states
  const [pageNumber, setPageNumber] = useState(selectedPage ?? 1);
  const [pageScale, setPageScale] = useState(1);
  const [activePage, setActivePage] = useState<IPageDataModel | null>();
  const [activeHighlights, setActiveHighlights] = useState<any>();
  const [imageStyle, setImageStyles] = useState<any>({
    width: "auto",
    height: "auto",
  });

  // consts
  const imageStyles: React.CSSProperties = useMemo(() => {
    const scaleOption = scaleTransformOptions.find(
      (each) => each?.scale === pageScale
    );
    return {
      "--scale": pageScale || 1,
      "--transformX": scaleOption?.x || 0,
      "--transformY": scaleOption?.y || 0,
      transform: `scale(${pageScale}) translate(${scaleOption?.x || 0}, ${
        scaleOption?.y || 0
      })`,
    } as React.CSSProperties;
  }, [pageScale]);

 




  // funcs - handlers
  function handleNext() {
    if (pages?.length && pageNumber < pages?.length) {
      setPageNumber((prev) => prev + 1);
    }
  }
  function handlePrevious() {
    if (pageNumber > 0) {
      setPageNumber((prev) => prev - 1);
    }
  }

  function handleZoomIn() {
    if (pageScale <= 1.25) {
      setPageScale(pageScale + 0.25);
    }
  }

  function handleZoomOut() {
    if (pageScale >= 0.25) {
      setPageScale(pageScale - 0.25);
    }
  }



  const scrollToField = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // const scrollToField = (id: string) => {
  //   const grandparent = document.getElementsByClassName('document-render-container')?.[0];
  //   const child = document.getElementById(id);
  
  //   if (grandparent && child) {
    
  //     const childRect = child.getBoundingClientRect();
  //     const grandparentRect = grandparent.getBoundingClientRect();

  //     console.log(childRect,grandparentRect);
  
      
  //     const scrollTop = childRect.top - grandparentRect.top + grandparent.scrollTop;
  
     
  //     grandparent.scrollTo({
  //       top: scrollTop,
  //       behavior: 'smooth' 
  //     });
  //   }
  // }

  // useEffect
  useEffect(() => {
    selectedPage && setPageNumber(selectedPage);
  }, [selectedPage]);

  useEffect(() => {
    if (activePage?.image_size && parentRef.current) {
      const { offsetWidth } = parentRef?.current;

      const height = (activePage?.image_size?.height * offsetWidth ) / activePage?.image_size?.width ?? offsetWidth

      setImageStyles({
        height: height  + "px",
        width: offsetWidth + "px",
      });
    }
  }, [activePage, pageScale]);

  useEffect(() => {
    if (pages && pages?.length > 0) {
      setActivePage(pages?.find((each) => each.page === pageNumber));
    }
  }, [pageNumber, pages]);

  useEffect(() => {
    if (highlights && highlights?.length > 0) {
      setActiveHighlights(
        highlights?.filter((each: any) => each.page === pageNumber) || []
      );
    }
  }, [pageNumber, highlights]);

  useEffect(() => {
    if (selectedHighlight?.id) {
      scrollToField(selectedHighlight?.id);
    }
  }, [selectedHighlight?.id]);

  const getValues = (parameter:string,dimension:string)=>{
    if(imageStyle && dimension && imageStyle?.[dimension] &&  imageStyle?.[dimension]!=="auto" && activePage?.image_size && activePage?.image_size?.[dimension]){
      const value = ((parseInt(parameter)*imageStyle?.[dimension]?.slice(0,-2)/activePage?.image_size?.[dimension]??1)) ;
      return value;
    }

    return parameter;
    
  }



  // console.log(imageStyles, "image");

  return (
    <>
    <div className="document-render-container">
      <div className="images-container" style={imageStyles} ref={parentRef}>
        {/* image */}
        {activePage?.image ? (
          <img
            src={`data:application/${"image/png"};base64,${activePage?.image}`}
            alt="activePage"
            className="active-image"
            style={imageStyle}
          />
        ) : (
          <></>
        )}

        {/* highlights */}
        {activeHighlights && activeHighlights?.length > 0 ? (
          activeHighlights?.map((each: any) =>
            each?.coordinates ? (
              <>
                {selectedHighlight?.id === each?.id}
                <div
                  id={each?.id}
                  key={each?.id}
                  className={`highlight-item ${
                    selectedHighlight && selectedHighlight?.id === each?.id
                      ? "active"
                      : ""
                  }`}
                  style={{
                    left: `${getValues(each?.coordinates?.x,"width")}px`,
                    top: `${getValues(each?.coordinates?.y,"height")}px`,
                    width: `${getValues(each?.coordinates?.w,"width")}px`,
                    height: `${getValues(each?.coordinates?.h,"height")}px`,
                  }}
                ></div>
              </>
            ) : (
              <></>
            )
          )
        ) : (
          <></>
        )}
      </div>
    </div>
    
    {pages && pages?.length > 0 ? (
        <div className="doc-options">
          <div className="page-number">
            {pageNumber === 1 ? (
              <></>
            ) : (
              <FontAwesomeIcon
                className="page-options page-up"
                icon={faAngleUp}
                onClick={handlePrevious}
              />
            )}
            {pageNumber === pages?.length ? (
              <></>
            ) : (
              <FontAwesomeIcon
                className="page-options page-down"
                icon={faAngleDown}
                onClick={handleNext}
              />
            )}
            <div className="page-text">
              Showing {pageNumber} of {pages?.length}
            </div>
          </div>
          <div className="zoom-container">
            <div className="zoom-value">{pageScale * 100} %</div>
            {pageScale >= 1.25 ? (
              <></>
            ) : (
              <FontAwesomeIcon
                className="zoom-options page-up"
                icon={faMagnifyingGlassPlus}
                onClick={handleZoomIn}
              />
            )}
            {pageScale <= 0.25 ? (
              <></>
            ) : (
              <FontAwesomeIcon
                className="zoom-options page-up"
                icon={faMagnifyingGlassMinus}
                onClick={handleZoomOut}
              />
            )}
          </div>
        </div>
      ) : (
        <></>
      )}
  </>
  );
};

export default DocumentRender;
